import { get } from 'lodash'

export const ADDON_OPTION = 'onetime'
export const SHOW_ADD_EDIT_PRODUCT_BUTTON = false

export const BUILD_ERRORS = {
    ADDON_WITHOUT_PACK: 'addon_without_pack',
    INVALID_ITEM_CODE: 'invalid_item_code',
    INVALID_QTY: 'invalid_qty',
    MISSING_PRICE_OPTION: 'missing_price_option'
}

const isOnetimeOnly = item => item?.isAddon === true || item?.type === 'single'

const toNumber = value => {
    if (value === null || value === undefined || value === '') {
        return NaN
    }
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : NaN
}

// detail-v2 sends the u23 totals display-formatted, so anything from four figures up arrives as
// "2,578". Number() reads that as NaN, which used to drop the summary back to the client-side total —
// a total that knows nothing about the order-level discount, so it silently overcharged the display
// by the discount amount. The digit count after the last comma decides whether that comma groups
// thousands (three digits) or marks decimals, so a four-figure total can never come out 100x off.
const moneyToNumber = value => {
    if (typeof value !== 'string') {
        return toNumber(value)
    }

    const compact = value.replace(/\s/g, '')
    const lastComma = compact.lastIndexOf(',')

    if (lastComma === -1) {
        return toNumber(compact)
    }

    const afterLastComma = compact.slice(lastComma + 1)
    const commaMarksDecimals = /^\d{1,2}$/.test(afterLastComma) && !compact.includes('.')

    return toNumber(commaMarksDecimals ? compact.replace(',', '.') : compact.replace(/,/g, ''))
}

export const resolvePriceOption = (item, paymentOption) => {
    if (!item) {
        return null
    }
    const optionKey = isOnetimeOnly(item) ? ADDON_OPTION : paymentOption
    return get(item, ['options', optionKey]) || null
}

export const resolveOptionKey = (item, paymentOption) => (isOnetimeOnly(item) ? ADDON_OPTION : paymentOption)

const sessionItem = (row, paymentOption) => {
    const qty = toNumber(row?.qty)
    if (Number.isNaN(qty) || qty <= 0) {
        return { error: BUILD_ERRORS.INVALID_QTY }
    }

    const option = resolvePriceOption(row, paymentOption)
    if (!option) {
        return { error: BUILD_ERRORS.MISSING_PRICE_OPTION }
    }

    const itemCode = option.item_code || row.item_code
    if (typeof itemCode !== 'string' || itemCode.trim() === '') {
        return { error: BUILD_ERRORS.INVALID_ITEM_CODE }
    }

    return { item: { item_code: itemCode, qty: qty, option: resolveOptionKey(row, paymentOption) } }
}

export const sessionItemsFrom = (rows, paymentOption) =>
    (Array.isArray(rows) ? rows : []).map(row => sessionItem(row, paymentOption).item).filter(Boolean)

export const buildSessionItems = (rows, paymentOption) => {
    const list = Array.isArray(rows) ? rows : []
    const errors = []
    const items = []

    const hasAddon = list.some(row => row?.isAddon === true)
    const hasSellablePack = list.some(row => row?.isAddon !== true && toNumber(row?.qty) > 0)

    if (hasAddon && !hasSellablePack) {
        errors.push(BUILD_ERRORS.ADDON_WITHOUT_PACK)
    }

    list.forEach(row => {
        const { item, error } = sessionItem(row, paymentOption)
        if (error) {
            errors.push(error)
            return
        }
        items.push(item)
    })

    return { items: errors.length > 0 ? [] : items, errors }
}

export const addonCode = item => `${item?.id ?? get(item, ['options', ADDON_OPTION, 'item_code']) ?? ''}`

export const dedupeAddonCatalog = addonItems => {
    const seen = new Set()

    return (Array.isArray(addonItems) ? addonItems : []).filter(item => {
        const code = addonCode(item)
        if (code === '' || seen.has(code)) {
            return false
        }
        seen.add(code)
        return true
    })
}

const hasUsablePrice = option => {
    return !Number.isNaN(toNumber(get(option, 'price.retail'))) || !Number.isNaN(toNumber(get(option, 'price.member')))
}

// `hasUsablePrice` keeps a row when *either* price key is a number, so the key the cart wants can still
// be the missing one — and then a product the customer pays for would render 0.00 AED. Reading the other
// key instead is wrong by at most the member discount; showing zero is wrong by the whole price.
// toNumber, not Number: `Number(null)` is 0, which reads as a real free price.
export const optionPrice = (option, preferredKey) => {
    const preferred = toNumber(get(option, ['price', preferredKey]))

    if (!Number.isNaN(preferred)) {
        return preferred
    }

    const fallback = toNumber(get(option, ['price', preferredKey === 'member' ? 'retail' : 'member']))

    return Number.isNaN(fallback) ? 0 : fallback
}

export const isOutOfStock = (item, paymentOption) => {
    const option = resolvePriceOption(item, paymentOption)
    if (!option || option.status === 'out_of_stock') {
        return true
    }
    const qty = toNumber(option.qty)
    return Number.isNaN(qty) || qty <= 0
}

export const buildAddonCatalog = (addonItems, paymentOption = ADDON_OPTION) =>
    dedupeAddonCatalog(addonItems)
        .filter(item => hasUsablePrice(resolvePriceOption(item, paymentOption)))
        .map(item => ({ ...item, isAddon: true, isOutOfStock: isOutOfStock(item, paymentOption) }))

export const memberSavings = (rows, paymentOption) => {
    return (Array.isArray(rows) ? rows : []).reduce((total, row) => {
        if (row?.isGift === true) {
            return total
        }
        const option = resolvePriceOption(row, paymentOption)
        const retail = toNumber(get(option, 'price.retail'))
        const member = toNumber(get(option, 'price.member'))
        const qty = toNumber(row?.qty)

        if (Number.isNaN(retail) || Number.isNaN(member) || Number.isNaN(qty)) {
            return total
        }

        return total + Math.max(0, retail - member) * qty
    }, 0)
}

const INVALID_ITEM_CODE = 'error_ufg_invalid_item_code'

const errorBodies = err => [
    get(err, 'response.data'),
    get(err, 'response.data.error'),
    get(err, 'response.data.error.error'),
    get(err, 'data'),
    err
]

// Message_v2 is the address endpoint's own list of dictionary keys for what it rejected. It comes back
// on a 200-with-success:false just as much as on a 4xx, so it has to be looked for in the same places
// as the invalid-item payload.
export const parseMessageV2 = payload =>
    (errorBodies(payload)
        .map(body => get(body, 'Message_v2'))
        .find(Array.isArray) || []
    ).filter(entry => typeof entry === 'string' && entry.trim() !== '')

export const parseInvalidItemError = err => {
    const match = errorBodies(err).find(body => {
        if (!body || typeof body !== 'object') {
            return false
        }
        const errors = Array.isArray(body.errors) ? body.errors : []
        return body.errName === INVALID_ITEM_CODE || errors.includes(INVALID_ITEM_CODE)
    })

    if (!match) {
        return { isInvalidItemCode: false, itemCodes: [] }
    }

    const codes = Array.isArray(match.itemCodes) ? match.itemCodes : []

    return {
        isInvalidItemCode: true,
        itemCodes: codes.filter(code => code !== null && code !== undefined && `${code}` !== '').map(code => `${code}`)
    }
}

export const formatTotals = data => {
    const total = get(data, 'ushop.u23.formatTotal')
    if (!total) {
        return null
    }

    const read = key => {
        const value = moneyToNumber(get(total, [key, 'value']))
        return Number.isNaN(value) ? null : value
    }

    return {
        subtotal: read('subtotal'),
        deliveryFee: read('delivery_fee'),
        discount: read('discount'),
        total: read('total_price')
    }
}

// The gift's display copy comes from the publish free-items list, matched by SKU — not from the
// dictionary. ARE has no u_all_temp_free_item_<sku> entries, so the old dictionary lookup always fell
// back to "Free gift" with no description. Matching on sku (not on list position) matters: Hydra
// grants 35950 and 36298 on dev, and only the SKU says which publish row describes which gift.
const publishedGift = (publishedFreeItems, itemCode) =>
    (Array.isArray(publishedFreeItems) ? publishedFreeItems : []).find(entry => {
        const sku = `${get(entry, 'sku', '') || get(entry, 'detail.item_code', '')}`
        return sku !== '' && sku === itemCode
    })

export const giftRows = (data, publishedFreeItems = []) => {
    const granted = get(data, 'hydra.marketing.freeItems', [])
    const keys = get(data, 'hydra.marketing.dictionaryKeys', [])

    return (Array.isArray(granted) ? granted : [])
        .map(entry => {
            const itemCode = `${get(entry, 'item_code', '')}`
            if (itemCode === '') {
                return null
            }

            const qty = toNumber(entry.quantity)
            const published = publishedGift(publishedFreeItems, itemCode)

            return {
                itemCode,
                qty: Number.isNaN(qty) || qty <= 0 ? 1 : qty,
                imageUrl: get(entry, 'details.image_url', ''),
                name: get(published, 'name') || null,
                description: get(published, 'description') || null,
                nameKey: keys.find(key => `${key}`.endsWith(itemCode)) || `u_all_temp_free_item_${itemCode}`
            }
        })
        .filter(Boolean)
}

export const hasGift = data => giftRows(data).length > 0

const parseItemList = raw => {
    if (!raw) {
        return []
    }

    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        return Array.isArray(parsed) ? parsed : []
    } catch (error) {
        return []
    }
}

export const parseAdditionalItems = detailPage => parseItemList(get(detailPage, 'additional_items'))

export const parseSessionItems = detailPage => parseItemList(get(detailPage, 'items'))

// Rebuilds cart rows from the item codes the Hydra session already holds. The cart is memory-only, so
// a reload used to leave the summary empty while the session still carried the order. Each row is the
// published product object, so prices, images and names resolve exactly as they do when the customer
// adds the product by hand. A code that matches neither catalogue is skipped rather than guessed.
export const cartRowsFromSession = (sessionItems, packProducts = [], addonProducts = []) => {
    const packs = Array.isArray(packProducts) ? packProducts : []
    const addons = Array.isArray(addonProducts) ? addonProducts : []

    return (Array.isArray(sessionItems) ? sessionItems : [])
        .map(entry => {
            const itemCode = `${get(entry, 'item_code', '')}`
            const qty = toNumber(get(entry, 'qty'))

            if (itemCode === '' || Number.isNaN(qty) || qty <= 0) {
                return null
            }

            const pack = packs.find(product =>
                Object.keys(get(product, 'options', {})).some(optionKey => `${get(product, ['options', optionKey, 'item_code'], '')}` === itemCode)
            )

            if (pack) {
                return { ...pack, qty }
            }

            const addon = addons.find(
                product => `${get(product, 'id', '')}` === itemCode || `${get(product, ['options', ADDON_OPTION, 'item_code'], '')}` === itemCode
            )

            return addon ? { ...addon, isAddon: true, qty } : null
        })
        .filter(Boolean)
}

export const findOosItems = (rows, paymentOption) => {
    return (Array.isArray(rows) ? rows : []).filter(row => resolvePriceOption(row, paymentOption) && isOutOfStock(row, paymentOption))
}

export const trackingItems = (rows, paymentOption, hasDiscount = false) => {
    return (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
        const option = resolvePriceOption(row, paymentOption)
        if (!option) {
            return acc
        }

        const optionKey = resolveOptionKey(row, paymentOption)

        const price = toNumber(get(option, ['price', hasDiscount ? 'member' : 'retail']))
        const pv = toNumber(option.pv)
        const qty = toNumber(row.qty)

        acc.push({
            item_id: row.id,
            item_code: option.item_code || row.item_code,
            item_name: get(row, 'name.english', ''),
            price: Number.isNaN(price) ? 0 : price,
            pv: Number.isNaN(pv) ? 0 : pv,
            qty: Number.isNaN(qty) ? 0 : qty,
            type: row.type,
            purchaseOption: optionKey
        })

        return acc
    }, [])
}
